import { useState } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { DiagnosticList } from '@/components/DiagnosticList/DiagnosticList';
import { Select } from '@/components/Select/Select';
import { TextArea } from '@/components/TextArea/TextArea';
import type { TemplateEnvelope, TemplateVersion } from '@/graphql/message-templates';
import { formatBag, parseBag, skeletonFromPaths, type VariableBag } from '@/lib/variable-bag';

import styles from './RenderPanel.module.css';

export interface RenderPanelProps {
  /** Newest first, as Keystone returns them. */
  versions: readonly TemplateVersion[];
  /** What the template's pointer names. Every write here mints at *n+1*, so
   *  this is the newest row today — but the panel reads the pointer rather
   *  than assuming that, since the two questions are not the same one. */
  currentVersionId: string | null;
  busy?: boolean;
  /** The last answer, or null before anything was asked. */
  render: TemplateEnvelope<string> | null;
  /** A refusal about the request — an unknown version, a template that is not
   *  yours — as opposed to anything in the envelope. */
  error?: string | null;
  onRender: (input: { versionNumber: number | null; variables: VariableBag | null }) => void;
}

const CURRENT = '';

/**
 * The one operation on this surface that substitutes anything.
 *
 * Keystone holds no applicants and no cases, so it cannot fill a template in:
 * the caller passes the values and Keystone puts them in the body. That is
 * what this panel is — the variable bag app-web would assemble from what is
 * already on its screen, typed by hand so the diagnostics can be exercised.
 *
 * Leaving the bag empty is not the same as an empty object here: it sends no
 * `variables` at all, which is how a caller with nothing to pass will call
 * this, and Keystone reads an absent bag as an empty one — so a template that
 * requires variables answers with one `MISSING_VARIABLE` per path rather than
 * an error about the request.
 */
export function RenderPanel({
  versions,
  currentVersionId,
  busy = false,
  render,
  error = null,
  onRender,
}: RenderPanelProps) {
  const [choice, setChoice] = useState<string>(CURRENT);
  const [bagText, setBagText] = useState('');
  const [bagError, setBagError] = useState<string | null>(null);

  const pinned = choice === CURRENT ? null : Number(choice);
  const version =
    (pinned === null
      ? versions.find((v) => v.id === currentVersionId)
      : versions.find((v) => v.versionNumber === pinned)) ?? null;
  const paths = version?.variablePaths ?? [];

  const onSubmit = () => {
    const { bag, error: parseError } = parseBag(bagText);
    setBagError(parseError);
    if (parseError) {
      return;
    }
    onRender({ versionNumber: pinned, variables: bag });
  };

  return (
    <section className={styles.panel}>
      <h4 className={styles.heading}>Render</h4>

      <div className={styles.controls}>
        <Select
          hint="Omitted, Keystone renders what is live. Pinned, it reproduces a message exactly as it was sent."
          label="Version"
          onChange={setChoice}
          options={[
            [CURRENT, 'Current version'],
            ...versions.map(
              (v) => [String(v.versionNumber), `Version ${v.versionNumber}`] as const,
            ),
          ]}
          value={choice}
        />
        <div className={styles.prefill}>
          <Button
            disabled={paths.length === 0}
            onClick={() => setBagText(formatBag(skeletonFromPaths(paths)))}
            type="button"
            variant="ghost"
          >
            Prefill from variable paths
          </Button>
          <p className={styles.pathsNote}>
            {paths.length === 0
              ? 'This version references no variables.'
              : `${version?.pathsAreRequired ? 'Requires' : 'May use'}: ${paths.join(', ')}`}
          </p>
        </div>
      </div>

      <TextArea
        error={bagError ?? undefined}
        face="mono"
        hint="A JSON object. Leave it empty to send no bag at all."
        label="Variable bag"
        onChange={(event) => setBagText(event.target.value)}
        placeholder={'{\n  "applicant": { "firstName": "Ada" }\n}'}
        rows={8}
        value={bagText}
      />

      <div className={styles.actions}>
        <Button loading={busy} onClick={onSubmit} type="button">
          Render
        </Button>
      </div>

      {error ? (
        <Callout tone="danger" title="Keystone refused the render">
          {error}
        </Callout>
      ) : null}

      {render ? (
        <>
          {render.result === null ? (
            <p className={styles.noResult}>
              No result — <code>result</code> is null exactly when <code>errors</code> is not empty.
            </p>
          ) : (
            <pre aria-label="Rendered message" className={styles.result}>
              {render.result}
            </pre>
          )}
          <DiagnosticList errors={render.errors} warnings={render.warnings} />
        </>
      ) : null}
    </section>
  );
}
